import {
  ApiProvider,
  useKioskFlow,
  useOnlineStatus,
  type KioskChoice,
  type SearchHousehold,
  type SearchPerson,
} from '@church/data'
import {
  faArrowRight,
  faCheck,
  faDeleteLeft,
  faHandshakeAngle,
  faMagnifyingGlass,
  faPrint,
  faRightFromBracket,
  faRotateLeft,
} from '@church/ui-icons'
import { createFileRoute } from '@tanstack/react-router'
import { formatKeypadPhone } from '@church/domain'
import { useEffect, useRef, useState } from 'react'
import { CheckinLabel, PrintableLabels } from '../components/CheckinLabel.tsx'
import { ConfirmButton, ErrorNote, Icon } from '../components/ui.tsx'
import {
  clearKioskConfig,
  createStationClient,
  loadKioskConfig,
  type KioskConfig,
} from '../lib/kiosk.ts'

/**
 * Kiosk mode: full screen, no app chrome, nobody signed in.
 *
 * The device authenticates as the station (lib/kiosk.ts), so this route sits
 * outside the signed-in shell entirely. Large targets, a phone-number keypad
 * for the self-service variant, labels printed the moment a family is checked
 * in, and a screen that clears itself — a foyer iPad is unattended between
 * families, and the last family's children's names must not wait there for
 * the next one.
 */
export const Route = createFileRoute('/kiosk')({
  component: KioskRoute,
})

function KioskRoute() {
  const [config, setConfig] = useState<KioskConfig | null>(() => loadKioskConfig())

  if (!config) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-base p-8">
        <div className="max-w-lg text-center">
          <h1 className="text-3xl font-bold text-content-default">
            This device is not a kiosk yet
          </h1>
          <p className="mt-3 text-content-muted">
            An administrator registers a station under Check-in → Setup and chooses “Use this device
            as the kiosk”. The station’s token is stored here, and this screen becomes the check-in
            desk.
          </p>
          <a href="/checkin" className="mt-6 inline-block font-medium text-content-brand">
            Go to Check-in setup
          </a>
        </div>
      </div>
    )
  }

  return (
    <ApiProvider client={createStationClient(config)}>
      <Kiosk
        config={config}
        onLeave={() => {
          clearKioskConfig()
          setConfig(null)
        }}
      />
    </ApiProvider>
  )
}

/**
 * Leaving kiosk mode is for staff, not families: it shows only after the
 * station's name is held for two seconds, and hides again after a while.
 */
function useHiddenExit() {
  const [shown, setShown] = useState(false)
  const timer = useRef<number | null>(null)
  useEffect(() => {
    if (!shown) return
    const hide = window.setTimeout(() => setShown(false), 15_000)
    return () => window.clearTimeout(hide)
  }, [shown])
  const cancel = () => {
    if (timer.current !== null) window.clearTimeout(timer.current)
    timer.current = null
  }
  return {
    shown,
    hide: () => setShown(false),
    handlers: {
      onPointerDown: () => {
        cancel()
        timer.current = window.setTimeout(() => setShown(true), 2000)
      },
      onPointerUp: cancel,
      onPointerLeave: cancel,
      onPointerCancel: cancel,
      onContextMenu: (event: { preventDefault: () => void }) => event.preventDefault(),
    },
  }
}

function Kiosk({ config, onLeave }: { config: KioskConfig; onLeave: () => void }) {
  const flow = useKioskFlow(config.stationId)
  const online = useOnlineStatus()
  const exit = useHiddenExit()
  // The countdown's length, as it started: the ring around Done empties over it.
  const countdownFrom = useRef(flow.countdown)
  if (flow.phase !== 'done') countdownFrom.current = flow.countdown
  const doneName = flow.household?.householdName

  // Labels print once, as soon as they exist.
  useEffect(() => {
    if (flow.phase === 'done' && flow.labels.length > 0) window.print()
  }, [flow.phase])

  return (
    <div
      className="safe-area flex min-h-screen flex-col bg-surface-base text-content-default"
      onPointerDown={flow.touch}
    >
      <header className="flex items-center justify-between border-b border-border-subtle bg-surface-raised px-4 py-4 sm:px-8">
        <div className="select-none" {...exit.handlers}>
          <p className="text-sm text-content-muted">Welcome — check in here</p>
          <p className="text-xl font-semibold">{config.stationName}</p>
        </div>
        <ServicePicker
          // Families at a self-service kiosk check in to the service running
          // now; only an assisted desk may pick another.
          editable={config.mode === 'assisted'}
          service={flow.service}
          services={flow.services}
          chosenId={flow.chosenServiceId}
          onChoose={flow.chooseService}
        />
        <div className="flex items-center gap-3 text-sm" data-testid="kiosk-status">
          <span
            className={`inline-block size-3 rounded-full ${
              flow.queued > 0
                ? 'bg-warning-solid'
                : online
                  ? 'bg-success-solid'
                  : 'bg-content-subtle'
            }`}
            aria-hidden
          />
          {flow.queued > 0
            ? `${flow.queued} waiting to send`
            : online
              ? 'Connected'
              : 'Offline — check-ins are kept on this device'}
        </div>
      </header>

      {flow.refused.length > 0 && (
        <section
          className="space-y-2 border-b-2 border-warning-solid bg-warning-soft px-4 py-3 text-warning-content sm:px-8"
          aria-live="polite"
          data-testid="kiosk-refused"
        >
          <p className="font-semibold">
            A volunteer is needed: {flow.refused.length === 1 ? 'a check-in was' : 'check-ins were'}{' '}
            not recorded
          </p>
          <ul className="space-y-2">
            {flow.refused.map((entry) => (
              <li key={entry.clientId} className="flex flex-wrap items-center gap-3">
                <span>
                  <strong>{entry.personName}</strong>{' '}
                  <Icon icon={faArrowRight} label="to" className="inline size-3" />{' '}
                  {entry.locationName} (label {entry.securityCode}): {entry.reason}
                </span>
                <button
                  type="button"
                  onClick={() => void flow.dismissRefused(entry.clientId)}
                  className="inline-flex items-center gap-2 rounded-lg border border-warning-solid px-3 py-1 text-sm font-medium"
                >
                  <Icon icon={faCheck} />
                  Volunteer has helped
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <main className="flex flex-1 flex-col items-center justify-center p-4 sm:p-8">
        {/* Each step arrives as its own screen (styles.css .screen-in). */}
        <div
          key={flow.phase}
          className="screen-in flex w-full flex-1 flex-col items-center justify-center"
        >
          {flow.phase === 'search' && (
            <SearchStep
              mode={config.mode}
              query={flow.query}
              onChange={flow.setQuery}
              onFind={flow.find}
              searching={flow.searching}
              error={flow.searchError}
              results={flow.results}
              noMatch={flow.noMatch}
              onChoose={flow.chooseHousehold}
            />
          )}

          {flow.phase === 'family' && flow.household && (
            <FamilyStep
              household={flow.household}
              selected={flow.selected}
              onToggle={flow.toggle}
              count={flow.selectedCount}
              onSubmit={() => void flow.submit()}
              submitting={flow.submitting}
              error={flow.submitError}
              onCancel={flow.reset}
            />
          )}

          {flow.phase === 'done' && (
            <div className="w-full max-w-2xl space-y-6 text-center">
              <span className="pop-in mx-auto flex size-24 items-center justify-center rounded-full bg-success-soft">
                <svg
                  viewBox="0 0 24 24"
                  aria-hidden
                  className="draw-check size-12 text-success-solid"
                >
                  <path
                    d="M5 12.5l4.5 4.5L19 7.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <h2 className="text-4xl font-bold">
                You’re checked in{doneName ? `, ${doneName}` : ''}
              </h2>
              <div className="flex flex-wrap justify-center gap-2">
                {flow.labels.map((label) => (
                  <span
                    key={label.clientId}
                    className="inline-flex items-center gap-2 rounded-full bg-surface-raised px-4 py-2 text-lg"
                  >
                    <span className="font-semibold">{label.personName.split(' ')[0]}</span>
                    <Icon icon={faArrowRight} className="size-4 text-content-muted" />
                    {label.locationName}
                  </span>
                ))}
              </div>
              <p className="text-lg text-content-muted">
                Take your labels. Keep the pickup tag — you will need its code to collect.
              </p>
              <div className="space-y-3 text-left">
                {flow.labels.map((label) => (
                  <CheckinLabel key={label.clientId} label={label} />
                ))}
              </div>
              <div className="flex justify-center gap-3">
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="inline-flex items-center gap-3 rounded-xl border border-border-default px-6 py-4 text-lg"
                >
                  <Icon icon={faPrint} className="size-5" />
                  Print again
                </button>
                <button
                  type="button"
                  onClick={flow.reset}
                  aria-label={`Done (closes in ${flow.countdown} seconds)`}
                  className="inline-flex items-center gap-3 rounded-xl bg-brand-solid px-6 py-4 text-lg font-semibold text-brand-on-solid active:scale-97"
                >
                  <CountdownRing left={flow.countdown} total={Math.max(1, countdownFrom.current)} />
                  Done
                </button>
              </div>
              <PrintableLabels labels={flow.labels} />
            </div>
          )}
        </div>
      </main>

      {exit.shown && (
        <footer className="flex items-center justify-end gap-3 px-4 py-3 sm:px-8">
          <span className="text-sm text-content-muted">Staff only</span>
          <ConfirmButton
            label="Leave kiosk mode"
            icon={faRightFromBracket}
            variant="ghost"
            title="Stop using this device as a kiosk?"
            description="The station token is removed from this device. An administrator will need to set it up again."
            confirmLabel="Leave kiosk mode"
            onConfirm={onLeave}
          />
        </footer>
      )}
    </div>
  )
}

/** The seconds left before the screen clears, as a ring that empties. */
function CountdownRing({ left, total }: { left: number; total: number }) {
  const circumference = 2 * Math.PI * 10
  return (
    <span className="relative inline-flex size-7 items-center justify-center">
      <svg viewBox="0 0 24 24" aria-hidden className="absolute inset-0 -rotate-90">
        <circle
          cx="12"
          cy="12"
          r="10"
          fill="none"
          stroke="currentColor"
          strokeOpacity="0.3"
          strokeWidth="2"
        />
        <circle
          cx="12"
          cy="12"
          r="10"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - left / total)}
          className="transition-[stroke-dashoffset] duration-1000 ease-linear"
        />
      </svg>
      <span className="text-sm tabular-nums">{left}</span>
    </span>
  )
}

/** Which service this station is checking in to — "every room" when none is set. */
function ServicePicker({
  editable,
  service,
  services,
  chosenId,
  onChoose,
}: {
  editable: boolean
  service: { id: string; name: string } | null
  services: { id: string; name: string }[]
  chosenId: string | null
  onChoose: (id: string | null) => void
}) {
  if (services.length === 0) return null
  if (!editable)
    return (
      <p className="text-lg font-medium" data-testid="kiosk-service">
        {service?.name ?? 'Every room'}
      </p>
    )
  return (
    <select
      aria-label="Service"
      data-testid="kiosk-service"
      value={chosenId ?? ''}
      onChange={(event) => onChoose(event.target.value || null)}
      className="rounded-xl border border-border-default bg-surface-raised px-3 py-2 text-base"
    >
      <option value="">{service && !chosenId ? `${service.name} (now)` : 'Automatic'}</option>
      {services.map((entry) => (
        <option key={entry.id} value={entry.id}>
          {entry.name}
        </option>
      ))}
    </select>
  )
}

const KEYPAD = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'] as const

function SearchStep({
  mode,
  query,
  onChange,
  onFind,
  searching,
  error,
  results,
  noMatch,
  onChoose,
}: {
  mode: 'self' | 'assisted'
  query: string
  onChange: (value: string) => void
  onFind: () => void
  searching: boolean
  error: unknown
  results: SearchHousehold[]
  noMatch: boolean
  onChoose: (household: SearchHousehold) => void
}) {
  return (
    <div className="w-full max-w-xl space-y-6 text-center">
      <h2 className="text-4xl font-bold">
        {mode === 'self' ? 'Enter your phone number' : 'Find your family'}
      </h2>
      <form
        onSubmit={(event) => {
          event.preventDefault()
          onFind()
        }}
        className="space-y-4"
      >
        <input
          value={mode === 'self' ? formatKeypadPhone(query) : query}
          onChange={(event) =>
            onChange(mode === 'self' ? event.target.value.replace(/\D/g, '') : event.target.value)
          }
          placeholder={mode === 'self' ? 'Phone number' : 'Surname or phone number'}
          inputMode={mode === 'self' ? 'tel' : 'text'}
          className="w-full rounded-2xl border-2 border-border-default bg-surface-raised px-6 py-5 text-center text-3xl tracking-wide"
          data-testid="kiosk-search"
          autoFocus
        />
        {mode === 'self' && (
          <div className="grid grid-cols-3 gap-3">
            {KEYPAD.map((key) =>
              key === '' ? (
                <span key="blank" />
              ) : (
                <button
                  key={key}
                  type="button"
                  onClick={() => onChange(key === '⌫' ? query.slice(0, -1) : query + key)}
                  aria-label={key === '⌫' ? 'Delete' : undefined}
                  className="flex items-center justify-center rounded-2xl border border-border-default bg-surface-raised py-5 text-3xl font-semibold transition-transform duration-(--duration-fast) active:scale-95 active:bg-surface-sunken"
                >
                  {key === '⌫' ? <Icon icon={faDeleteLeft} className="size-8" /> : key}
                </button>
              ),
            )}
          </div>
        )}
        <button
          type="submit"
          disabled={query.trim().length < 2 || searching}
          className="flex w-full items-center justify-center gap-3 rounded-2xl bg-brand-solid py-5 text-2xl font-semibold text-brand-on-solid disabled:opacity-50"
        >
          <Icon icon={faMagnifyingGlass} className="size-6" />
          {searching ? 'Looking you up…' : 'Find'}
        </button>
      </form>
      <ErrorNote error={error} />
      {noMatch && (
        <p className="text-lg text-content-muted">
          We could not find you. A volunteer at the desk can help.
        </p>
      )}
      {results.length > 1 && (
        <div className="space-y-3">
          <p className="text-lg text-content-muted">Which family?</p>
          {results.map((household) => (
            <button
              key={household.householdId}
              type="button"
              onClick={() => onChoose(household)}
              className="w-full rounded-2xl border border-border-default bg-surface-raised px-6 py-4 text-left text-xl"
            >
              {household.householdName ?? 'Household'}
              <span className="block text-base text-content-muted">
                {household.members.map((member) => member.firstName).join(', ')}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function FamilyStep({
  household,
  selected,
  onToggle,
  count,
  onSubmit,
  submitting,
  error,
  onCancel,
}: {
  household: SearchHousehold
  selected: Record<string, KioskChoice>
  onToggle: (
    person: SearchPerson,
    location?: { id: string; name: string },
    serving?: boolean,
  ) => void
  count: number
  onSubmit: () => void
  submitting: boolean
  error: unknown
  onCancel: () => void
}) {
  return (
    <div className="w-full max-w-3xl space-y-6">
      <h2 className="text-center text-4xl font-bold">
        {household.householdName ?? 'Your family'} — who is here today?
      </h2>
      <div className="space-y-3">
        {household.members.map((person) => {
          const chosen = selected[person.personId]
          const open = person.openCheckin
          return (
            <div
              key={person.personId}
              className={`rounded-2xl border-2 p-5 ${
                chosen
                  ? 'border-brand-solid bg-brand-soft'
                  : 'border-border-subtle bg-surface-raised'
              }`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-2xl font-semibold">
                    {person.firstName} {person.lastName}
                  </p>
                  <p className="text-content-muted">
                    {open
                      ? open.kind === 'volunteer'
                        ? `Serving in ${open.locationName}`
                        : `Already in ${open.locationName}`
                      : chosen
                        ? chosen.serving
                          ? `Serving in ${chosen.name}`
                          : chosen.name
                        : person.eligibleLocations.length === 0
                          ? person.isChild
                            ? 'No room for their age — please see a volunteer'
                            : person.serveLocations.length > 0
                              ? 'Serving today? Tap a room'
                              : 'Adult'
                          : 'Tap a room to check in'}
                  </p>
                </div>
                {!open && (
                  <div className="flex flex-wrap gap-2">
                    {person.eligibleLocations.map((location) => (
                      <button
                        key={location.id}
                        type="button"
                        disabled={location.isFull}
                        onClick={() => onToggle(person, { id: location.id, name: location.name })}
                        className={`rounded-xl px-5 py-4 text-lg font-medium disabled:opacity-40 ${
                          chosen?.id === location.id && !chosen.serving
                            ? 'bg-brand-solid text-brand-on-solid'
                            : 'border border-border-default bg-surface-raised'
                        }`}
                      >
                        {location.name}
                        {location.isFull ? ' (full)' : ''}
                      </button>
                    ))}
                    {person.serveLocations.map((location) => (
                      <button
                        key={`serve-${location.id}`}
                        type="button"
                        onClick={() =>
                          onToggle(person, { id: location.id, name: location.name }, true)
                        }
                        className={`inline-flex items-center gap-2 rounded-xl px-5 py-4 text-lg font-medium ${
                          chosen?.id === location.id && chosen.serving
                            ? 'bg-brand-solid text-brand-on-solid'
                            : 'border border-dashed border-border-default bg-surface-raised'
                        }`}
                      >
                        <Icon icon={faHandshakeAngle} className="size-5" />
                        Serve in {location.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
      <ErrorNote error={error} />
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-3 rounded-2xl border border-border-default px-6 py-5 text-xl"
        >
          <Icon icon={faRotateLeft} className="size-5" />
          Start over
        </button>
        <button
          type="button"
          disabled={count === 0 || submitting}
          onClick={onSubmit}
          className="flex flex-1 items-center justify-center gap-3 rounded-2xl bg-brand-solid py-5 text-2xl font-semibold text-brand-on-solid disabled:opacity-50"
          data-testid="kiosk-submit"
        >
          <Icon icon={faCheck} className="size-6" />
          {submitting ? 'Checking in…' : `Check in ${count}`}
        </button>
      </div>
    </div>
  )
}
